<script setup lang="ts">
import type { Component } from 'vue';

type Variant = 'primary' | 'secondary' | 'ghost' | 'soft' | 'danger';
type Size = 'sm' | 'md' | 'lg';
type Shape = 'square' | 'circle';

withDefaults(
  defineProps<{
    as?: string | Component;
    variant?: Variant;
    size?: Size;
    shape?: Shape;
    disabled?: boolean;
  }>(),
  {
    as: 'button',
    variant: 'secondary',
    size: 'md',
    shape: 'square',
    disabled: false
  }
);
</script>

<template>
  <component
    :is="as"
    v-bind="$attrs"
    :disabled="as === 'button' ? disabled : undefined"
    :aria-disabled="disabled || undefined"
    :class="[
      'ui-icon-button',
      `ui-icon-button--${variant}`,
      `ui-icon-button--${size}`,
      shape === 'circle' && 'ui-icon-button--circle',
      disabled && 'ui-icon-button--disabled'
    ]"
  >
    <slot />
  </component>
</template>

<style scoped>
.ui-icon-button {
  --ui-icon-button-icon-size: 1rem;
  --ui-icon-button-bg: var(--ui-card-bg);
  --ui-icon-button-border: var(--ui-border);
  --ui-icon-button-color: var(--ui-foreground);
  --ui-icon-button-hover-bg: var(--ui-card-bg-muted);
  --ui-icon-button-hover-border: var(--ui-border-strong);
  --ui-icon-button-hover-color: var(--ui-icon-button-color);
  --ui-icon-button-hover-shadow: var(--ui-control-shadow);
  --ui-icon-button-shadow: inset 0 1px 0 color-mix(in srgb, var(--ui-foreground-inverse) 26%, transparent);

  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--ui-icon-button-border);
  border-radius: var(--ui-control-radius);
  background: var(--ui-icon-button-bg);
  box-shadow: var(--ui-icon-button-shadow);
  color: var(--ui-icon-button-color);
  cursor: pointer;
  text-decoration: none;
  transition:
    background 0.16s ease,
    background-color 0.16s ease,
    border-color 0.16s ease,
    box-shadow 0.16s ease,
    color 0.16s ease,
    transform 0.16s ease;
}

.ui-icon-button :deep(svg),
.ui-icon-button :slotted(svg) {
  display: block;
  width: var(--ui-icon-button-icon-size);
  height: var(--ui-icon-button-icon-size);
  flex: 0 0 var(--ui-icon-button-icon-size);
  color: currentColor;
  pointer-events: none;
  stroke: currentColor;
}

.ui-icon-button--circle {
  border-radius: var(--ui-radius-full);
}

.ui-icon-button--sm {
  --ui-icon-button-icon-size: 1rem;

  width: 2.375rem;
  height: 2.375rem;
  font-size: 0.85rem;
}

.ui-icon-button--md {
  --ui-icon-button-icon-size: 1.1rem;

  width: 2.75rem;
  height: 2.75rem;
  font-size: 0.95rem;
}

.ui-icon-button--lg {
  --ui-icon-button-icon-size: 1.2rem;

  width: 3.125rem;
  height: 3.125rem;
  font-size: 1rem;
}

.ui-icon-button:not(.ui-icon-button--disabled):hover {
  border-color: var(--ui-icon-button-hover-border);
  background: var(--ui-icon-button-hover-bg);
  box-shadow: var(--ui-icon-button-hover-shadow);
  color: var(--ui-icon-button-hover-color);
  transform: translateY(-1px);
}

.ui-icon-button:not(.ui-icon-button--disabled):active {
  transform: translateY(0);
}

.ui-icon-button:focus-visible {
  outline: none;
  box-shadow: var(--ui-icon-button-shadow), var(--ui-focus-ring);
}

.ui-icon-button--primary {
  --ui-icon-button-bg: linear-gradient(
    180deg,
    color-mix(in srgb, var(--ui-accent) 84%, var(--ui-foreground-inverse) 16%) 0%,
    color-mix(in srgb, var(--ui-accent) 88%, var(--ui-foreground) 12%) 100%
  );
  --ui-icon-button-border: color-mix(in srgb, var(--ui-border-strong) 58%, var(--ui-accent) 42%);
  --ui-icon-button-color: var(--ui-on-accent);
  --ui-icon-button-hover-bg: linear-gradient(180deg, var(--ui-accent), color-mix(in srgb, var(--ui-accent) 88%, var(--ui-foreground) 12%));
  --ui-icon-button-hover-border: color-mix(in srgb, var(--ui-border-strong) 46%, var(--ui-accent) 54%);
  --ui-icon-button-shadow:
    0 10px 28px color-mix(in srgb, var(--ui-accent) 24%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--ui-on-accent) 24%, transparent);
}

.ui-icon-button--secondary {
  --ui-icon-button-bg: linear-gradient(180deg, var(--ui-card-bg), color-mix(in srgb, var(--ui-card-bg-muted) 58%, var(--ui-card-bg)));
  --ui-icon-button-border: var(--ui-border);
  --ui-icon-button-color: var(--ui-foreground);
  --ui-icon-button-hover-bg: linear-gradient(180deg, var(--ui-card-bg), var(--ui-card-bg-muted));
  --ui-icon-button-hover-border: var(--ui-border-strong);
}

.ui-icon-button--soft {
  --ui-icon-button-bg: color-mix(in srgb, var(--ui-accent) 10%, var(--ui-card-bg));
  --ui-icon-button-border: color-mix(in srgb, var(--ui-accent) 24%, var(--ui-border));
  --ui-icon-button-color: color-mix(in srgb, var(--ui-foreground) 80%, var(--ui-accent) 20%);
  --ui-icon-button-hover-bg: color-mix(in srgb, var(--ui-accent) 14%, var(--ui-card-bg));
  --ui-icon-button-hover-border: color-mix(in srgb, var(--ui-accent) 34%, var(--ui-border));
}

.ui-icon-button--ghost {
  --ui-icon-button-bg: transparent;
  --ui-icon-button-border: transparent;
  --ui-icon-button-shadow: none;
  --ui-icon-button-hover-bg: color-mix(in srgb, var(--ui-surface-muted) 74%, transparent);
  --ui-icon-button-hover-border: color-mix(in srgb, var(--ui-border) 78%, transparent);
}

.ui-icon-button--danger {
  --ui-icon-button-bg: linear-gradient(180deg, color-mix(in srgb, var(--ui-danger) 86%, var(--ui-foreground-inverse) 14%), var(--ui-danger));
  --ui-icon-button-border: var(--ui-danger);
  --ui-icon-button-color: var(--ui-on-danger);
  --ui-icon-button-hover-bg: linear-gradient(180deg, var(--ui-danger), color-mix(in srgb, var(--ui-danger) 86%, var(--ui-foreground) 14%));
  --ui-icon-button-hover-border: color-mix(in srgb, var(--ui-danger) 86%, var(--ui-foreground) 14%);
  --ui-icon-button-shadow:
    0 10px 28px color-mix(in srgb, var(--ui-danger) 20%, transparent),
    inset 0 1px 0 color-mix(in srgb, var(--ui-on-danger) 22%, transparent);
}

.ui-icon-button--disabled {
  opacity: 0.58;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

@media (prefers-reduced-motion: reduce) {
  .ui-icon-button {
    transition: none;
  }
}

@media (pointer: coarse) {
  .ui-icon-button--sm {
    width: var(--ui-touch-target-min);
    height: var(--ui-touch-target-min);
  }
}
</style>
